import type { Studio } from "@/types";

/** Demo / dev fallbacks when `studio.address` is not yet on the API payload. */
export const STUDIO_ADDRESS_FALLBACK_BY_ID: Record<string, string> = {
  "studio-1": "Hauptstraße 42, 10117 Berlin, Germany",
  "studio-2": "Friedrichstraße 112, 10117 Berlin, Germany",
};

const avatarColors = [
  "bg-[hsl(200_60%_90%)] text-[hsl(200_60%_35%)]",
  "bg-[hsl(160_50%_90%)] text-[hsl(160_50%_30%)]",
  "bg-[hsl(330_50%_92%)] text-[hsl(330_50%_35%)]",
  "bg-[hsl(38_60%_90%)] text-[hsl(38_60%_35%)]",
  "bg-[hsl(260_40%_92%)] text-[hsl(260_40%_35%)]",
  "bg-[hsl(180_40%_90%)] text-[hsl(180_40%_30%)]",
];

export function studioListAvatarColorClass(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return avatarColors[Math.abs(hash) % avatarColors.length];
}

export function studioListInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return (parts[0]?.[0] ?? "?").toUpperCase();
}

/**
 * Flex cross-axis alignment for avatar + primary text (+ optional second line) in studio lists/tables.
 * Use `items-center` when only the name line is shown so it lines up with the circular avatar.
 */
export function studioAvatarRowAlign(hasSecondaryLine: boolean): "items-start" | "items-center" {
  return hasSecondaryLine ? "items-start" : "items-center";
}

function rawStudioLocation(studio: Studio): string {
  const a = studio.address?.trim();
  if (a) return a;
  return STUDIO_ADDRESS_FALLBACK_BY_ID[studio.id] ?? "";
}

/**
 * Address line + city line (e.g. filters). Prefer {@link getStudioLocationSingleLine} for list UI.
 */
export function getStudioAddressLines(studio: Studio): { addressLine: string; cityLine: string | null } {
  const raw = rawStudioLocation(studio);
  if (!raw) return { addressLine: "", cityLine: null };
  const parts = raw.split(",").map((s) => s.trim()).filter(Boolean);
  if (parts.length >= 2) {
    const cityLine = parts[parts.length - 1] ?? null;
    const addressLine = parts.slice(0, -1).join(", ");
    return { addressLine, cityLine };
  }
  return { addressLine: raw, cityLine: null };
}

/** One line for list/detail subtitle — matches Chats preview row (truncate, width-driven ellipsis). */
export function getStudioLocationSingleLine(studio: Studio): string {
  return rawStudioLocation(studio);
}

/** AT/DE-style: leading 4–5 digit postal code before city name. */
function stripLeadingPostalCode(segment: string): string {
  const t = segment.trim();
  const m = t.match(/^\d{4,5}\s+(.+)$/);
  return (m?.[1] ?? t).trim();
}

function normalizeCityKey(city: string): string {
  return city
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{M}/gu, "");
}

/** When the address has no explicit country segment, infer from known city names (demo + DACH-focused). */
function inferCountryFromCityName(city: string): string | null {
  const k = normalizeCityKey(city);
  const at = new Set(["vienna", "wien", "salzburg", "graz", "linz", "innsbruck", "klagenfurt"]);
  if (at.has(k)) return "Austria";
  const de = new Set([
    "berlin",
    "munich",
    "munchen",
    "hamburg",
    "cologne",
    "koln",
    "koeln",
    "frankfurt",
    "stuttgart",
    "dusseldorf",
    "duesseldorf",
    "dortmund",
    "essen",
    "leipzig",
    "dresden",
    "hannover",
    "nuremberg",
    "nurnberg",
    "bremen",
  ]);
  if (de.has(k)) return "Germany";
  return null;
}

/**
 * Filter bucket label: **City, Country** (never `PLZ City` alone when we can strip PLZ or read country).
 */
export function getStudioLocationCityCountry(studio: Studio): string | null {
  const raw = rawStudioLocation(studio);
  if (!raw) return null;
  const parts = raw.split(",").map((s) => s.trim()).filter(Boolean);
  if (parts.length >= 3) {
    const country = parts[parts.length - 1].trim();
    const city = stripLeadingPostalCode(parts[parts.length - 2] ?? "");
    if (!city) return country || null;
    return `${city}, ${country}`;
  }
  if (parts.length === 2) {
    const city = stripLeadingPostalCode(parts[1]);
    if (!city) return null;
    const country = inferCountryFromCityName(city);
    return country ? `${city}, ${country}` : city;
  }
  const one = stripLeadingPostalCode(parts[0] ?? "");
  if (!one) return null;
  const country = inferCountryFromCityName(one);
  return country ? `${one}, ${country}` : one;
}
